import type { ZodTypeAny } from 'zod'
import z from 'zod'

export const stringValidationSchema = (label: string, min: number, max: number) => z.string()
  .min(min, `The ${label} must be at least ${min} character long`)
  .max(max, `The ${label} must be at most ${max} characters long`)

export interface ValidationSchema {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object'
  min?: number
  max?: number
  pattern?: string
  nullable?: boolean
  optional?: boolean
  default?: any
  isLiteral?: boolean
  isEnum?: boolean
  options?: string[]
}
function zodBuilderType(type: ZodTypeAny) {
  let zType = type
  return {
    add<T extends ZodTypeAny>(cb: (zT: T) => ZodTypeAny) {
      zType = cb(zType as T)
      return this
    },
    build() {
      return zType
    },
  }
}

export const zodValidationBuilder = (label: string, schema?: ValidationSchema): (value: any) => string | true => {
  if (!schema)
    return () => true
  let schemaZod: any
  const validationsFn = (value: any) => {
    try {
      if (!schemaZod)
        return true
      schemaZod.parse(value)
      return true
    }
    catch (error) {
      if (error instanceof z.ZodError)
        return error.flatten().formErrors.join(', ')
      return (error as Error).message
    }
  }
  if (schema.type === 'string') {
    const builder = zodBuilderType(z.string())
    if (schema.isLiteral) {
      if (schema.options) {
        builder.add((string) => {
          for (const option of schema.options!)
            string.or(z.literal(option))
          return string
        })
        schemaZod = builder.build()

        return validationsFn
      }
    }

    if (schema.min)
      builder.add<z.ZodString>(string => string.min(schema.min!, `${label} must be at least ${schema.min} characters long`))
    if (schema.max)
      builder.add<z.ZodString>(string => string.max(schema.max!, `${label} must be at most ${schema.max} characters long`))
    if (schema.pattern)
      builder.add<z.ZodString>(string => string.regex(new RegExp(schema.pattern!), `${label} must match the pattern ${schema.pattern}`))

    if (schema.optional)
      builder.add(string => string.optional())
    if (schema.nullable)
      builder.add<z.ZodString>(string => string.nullable())
    if (schema.default !== undefined)
      builder.add<z.ZodString>(string => string.default(schema.default))
    schemaZod = builder.build()
  }
  if (schema.type === 'number') {
    const builder = zodBuilderType(z.number())
    if (schema.optional)
      builder.add<z.ZodNumber>(number => number.optional())
    if (schema.min)
      builder.add<z.ZodNumber>(number => number.min(schema.min!, `${label} must be at least ${schema.min}`))
    if (schema.max)
      builder.add<z.ZodNumber>(number => number.max(schema.max!, `${label} must be at most ${schema.max}`))
    if (schema.default !== undefined)
      builder.add<z.ZodNumber>(number => number.default(schema.default))
    schemaZod = builder.build()
  }
  if (schema.type === 'boolean') {
    const builder = zodBuilderType(z.boolean())
    if (schema.optional)
      builder.add<z.ZodBoolean>(boolean => boolean.optional())
    if (schema.default !== undefined)
      builder.add<z.ZodBoolean>(boolean => boolean.default(schema.default))
    schemaZod = builder.build()
  }

  return validationsFn
}
